import axios from 'axios';
import { getAuthHeader } from './authService';
import API_CONFIG from '../config';
import { API_BASE_URL } from '../config/constants';
import api from './api';
import NOTIFICATION_CONFIG from '../config/notificationConfig';

const API_URL = API_CONFIG.BASE_URL;
const NOTIFICATIONS_URL = `${API_URL}/notifications`;
const SALES_ORDERS_URL = `${API_URL}/sales-orders`;
const PURCHASE_ORDERS_URL = `${API_URL}/purchase-orders`;

const NOTIFICATION_URL = '/api/notifications';



// Export standalone functions for direct import
export const getNotifications = () => {
  return api.get(NOTIFICATION_URL);
};

export const markNotificationAsRead = (notificationId) => {
  return api.put(`${NOTIFICATION_URL}/${notificationId}/read`);
};

export const deleteNotification = (notificationId) => {
  return api.delete(`${NOTIFICATION_URL}/${notificationId}`);
};

/**
 * Service for managing notifications
 */
const notificationService = {
  /**
   * Get all unread notifications for the current user
   * @returns {Promise<Array>} List of unread notifications
   */
  getUnreadNotifications: async () => {
    try {
      const response = await axios.get(`${NOTIFICATIONS_URL}/unread`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching unread notifications:', error);
      // Return empty array if API endpoint is not available yet
      return [];
    }
  },

  /**
   * Mark a notification as read
   * @param {number} id - Notification ID
   * @returns {Promise<Object>} Updated notification
   */
  markAsRead: async (id) => {
    try {
      const response = await axios.patch(`${NOTIFICATIONS_URL}/${id}/read`, {}, {
        headers: getAuthHeader()
      });
      
      // Update the notification in cache if present
      if (notificationService.notificationsCache.data.length > 0) {
        notificationService.notificationsCache.data = notificationService.notificationsCache.data.map(
          notification => notification.id === id ? { ...notification, read: true } : notification
        );
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error marking notification ${id} as read:`, error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read
   * @returns {Promise<Object>} Response data
   */
  markAllAsRead: async () => {
    try {
      const response = await axios.post(`${NOTIFICATIONS_URL}/mark-all-read`, {}, {
        headers: getAuthHeader()
      });
      
      // Update all notifications in cache to be read
      if (notificationService.notificationsCache.data.length > 0) {
        notificationService.notificationsCache.data = notificationService.notificationsCache.data.map(
          notification => ({ ...notification, read: true })
        );
      }
      
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  /**
   * Get recent sales orders for notifications (temporary until backend notification API is ready)
   * @param {number} limit - Maximum number of orders to retrieve
   * @returns {Promise<Array>} List of recent sales orders
   */
  getRecentSalesOrders: async (limit = 5) => {
    // Check if sales orders API is disabled
    if (!NOTIFICATION_CONFIG.enableSalesOrdersAPI) {
      console.warn('Sales orders API is disabled in configuration');
      return notificationService.cachedSalesOrders || [];
    }

    try {
      // Check if we've had recent failures and implement circuit breaker
      if (notificationService.salesOrdersFailureCount >= NOTIFICATION_CONFIG.maxRetries) {
        const timeSinceLastFailure = Date.now() - notificationService.lastSalesOrdersFailure;
        if (timeSinceLastFailure < 300000) { // 5 minutes
          console.warn('Sales orders API circuit breaker active, using cached data');
          return notificationService.cachedSalesOrders || [];
        } else {
          // Reset failure count after 5 minutes
          notificationService.salesOrdersFailureCount = 0;
        }
      }

      const response = await axios.get(SALES_ORDERS_URL, {
        params: {
          page: 0,
          size: limit,
          sort: 'orderDate',
          direction: 'DESC'
        },
        headers: getAuthHeader(),
        timeout: 10000 // 10 second timeout
      });
      
      // Reset failure count on success
      notificationService.salesOrdersFailureCount = 0;
      
      const orders = response.data.content || response.data || [];
      const salesOrderNotifications = orders.map(order => ({
        id: order.id,
        type: 'SALES_ORDER',
        title: `New order #${order.id}`,
        message: `New sales order #${order.id} for $${order.total.toFixed(2)}`,
        createdAt: order.orderDate || new Date().toISOString(),
        read: false,
        data: {
          orderId: order.id,
          total: order.total,
          customerName: order.customer?.name || 'Customer'
        }
      }));
      
      // Cache successful result
      notificationService.cachedSalesOrders = salesOrderNotifications;
      return salesOrderNotifications;
    } catch (error) {
      // Increment failure count and record failure time
      notificationService.salesOrdersFailureCount = (notificationService.salesOrdersFailureCount || 0) + 1;
      notificationService.lastSalesOrdersFailure = Date.now();
      
      if (error.response?.status === 403) {
        console.warn('Sales orders API access denied (403), using cached data');
      } else {
        console.error('Error fetching recent sales orders:', error.message);
      }
      
      // Return cached data if available
      return notificationService.cachedSalesOrders || [];
    }
  },

  /**
   * Get recent purchase orders for notifications
   * @param {number} limit - Maximum number of orders to retrieve
   * @returns {Promise<Array>} List of recent purchase orders
   */
  getRecentPurchaseOrders: async (limit = 5) => {
    // Check if purchase orders API is disabled
    if (!NOTIFICATION_CONFIG.enablePurchaseOrdersAPI) {
      return notificationService.cachedPurchaseOrders || [];
    }

    try {
      // Circuit breaker check
      if (notificationService.purchaseOrdersFailureCount >= NOTIFICATION_CONFIG.maxRetries) {
        const timeSinceLastFailure = Date.now() - notificationService.lastPurchaseOrdersFailure;
        if (timeSinceLastFailure < NOTIFICATION_CONFIG.circuitBreakerTimeout) {
          return notificationService.cachedPurchaseOrders || [];
        }
        notificationService.purchaseOrdersFailureCount = 0;
      }

      const response = await axios.get(PURCHASE_ORDERS_URL, {
        params: {
          page: 0,
          size: limit,
          sort: 'orderDate',
          direction: 'DESC'
        },
        headers: getAuthHeader(),
        timeout: NOTIFICATION_CONFIG.requestTimeout
      });
      
      notificationService.purchaseOrdersFailureCount = 0;
      
      const orders = response.data.content || response.data || [];
      const purchaseOrderNotifications = orders.map(order => ({
        id: `purchase-${order.id}`,
        type: 'PURCHASE_ORDER',
        title: `New purchase order #${order.id}`,
        message: `New purchase order from ${order.supplier?.name || 'Supplier'} for $${order.total.toFixed(2)}`,
        createdAt: order.orderDate || new Date().toISOString(),
        read: false,
        data: {
          orderId: order.id,
          total: order.total,
          supplierName: order.supplier?.name || 'Supplier'
        }
      }));
      
      notificationService.cachedPurchaseOrders = purchaseOrderNotifications;
      return purchaseOrderNotifications;
    } catch (error) {
      notificationService.purchaseOrdersFailureCount = (notificationService.purchaseOrdersFailureCount || 0) + 1;
      notificationService.lastPurchaseOrdersFailure = Date.now();
      return notificationService.cachedPurchaseOrders || [];
    }
  },

  /**
   * Get low stock items for notifications (temporary until backend notification API is ready)
   * @returns {Promise<Array>} List of low stock notifications
   */
  getLowStockNotifications: async () => {
    // Check if low stock API is disabled
    if (!NOTIFICATION_CONFIG.enableLowStockAPI) {
      console.warn('Low stock API is disabled in configuration');
      return notificationService.cachedLowStock || [];
    }

    try {
      // Check if we've had recent failures and implement circuit breaker
      if (notificationService.lowStockFailureCount >= NOTIFICATION_CONFIG.maxRetries) {
        const timeSinceLastFailure = Date.now() - notificationService.lastLowStockFailure;
        if (timeSinceLastFailure < 300000) { // 5 minutes
          console.warn('Low stock API circuit breaker active, using cached data');
          return notificationService.cachedLowStock || [];
        } else {
          // Reset failure count after 5 minutes
          notificationService.lowStockFailureCount = 0;
        }
      }

      const response = await axios.get(`${API_URL}/products/low-stock`, {
        headers: getAuthHeader(),
        timeout: 10000 // 10 second timeout
      });
      
      // Reset failure count on success
      notificationService.lowStockFailureCount = 0;
      
      const products = response.data || [];
      const outOfStockCount = products.filter(p => p.quantity <= 0).length;
      const lowStockCount = products.length - outOfStockCount;
      
      const lowStockNotifications = [];
      
      if (products.length > 0) {
        let message = '';
        if (outOfStockCount > 0 && lowStockCount > 0) {
          message = `${outOfStockCount} products out of stock and ${lowStockCount} running low`;
        } else if (outOfStockCount > 0) {
          message = `${outOfStockCount} products are out of stock`;
        } else {
          message = `${lowStockCount} products are running low on stock`;
        }

        lowStockNotifications.push({
          id: 'low-stock-' + Date.now(),
          type: 'LOW_STOCK',
          title: outOfStockCount > 0 ? 'Stock Alert' : 'Low stock alert',
          message: message,
          createdAt: new Date().toISOString(),
          read: false,
          data: {
            count: products.length,
            outOfStockCount,
            lowStockCount,
            products: products.map(p => ({ id: p.id, name: p.name, quantity: p.quantity }))
          }
        });
      }
      
      // Cache successful result
      notificationService.cachedLowStock = lowStockNotifications;
      return lowStockNotifications;
    } catch (error) {
      // Increment failure count and record failure time
      notificationService.lowStockFailureCount = (notificationService.lowStockFailureCount || 0) + 1;
      notificationService.lastLowStockFailure = Date.now();
      
      if (error.response?.status === 403) {
        console.warn('Low stock API access denied (403), using cached data');
      } else {
        console.error('Error fetching low stock notifications:', error.message);
      }
      
      // Return cached data if available
      return notificationService.cachedLowStock || [];
    }
  },

  /**
   * Format notification message based on notification type
   * @param {Object} notification - Notification object
   * @returns {Object} Formatted notification with title and message
   */
  formatNotification: (notification) => {
    switch (notification.type) {
      case 'SALES_ORDER':
        return {
          ...notification,
          title: `New order #${notification.data.orderId}`,
          message: `New sales order from ${notification.data.customerName} for $${notification.data.total.toFixed(2)}`
        };
      case 'PURCHASE_ORDER':
        return {
          ...notification,
          title: `New purchase order #${notification.data.orderId}`,
          message: `New purchase order from ${notification.data.supplierName} for $${notification.data.total.toFixed(2)}`
        };
      case 'LOW_STOCK':
        return {
          ...notification,
          title: 'Low stock alert',
          message: `${notification.data.count} products are running low on stock`
        };
      default:
        return notification;
    }
  },

  // Cache for getAllNotifications to avoid excessive API calls
  notificationsCache: {
    data: [],
    timestamp: 0,
    expiryMs: NOTIFICATION_CONFIG.cacheExpiry
  },

  /**
   * Get all notifications (combines API and local sources)
   * This is a temporary implementation until the backend notification API is fully ready
   * @returns {Promise<Array>} Combined list of notifications
   */
  getAllNotifications: async () => {
    try {
      const now = Date.now();
      
      // Return cached data if it's still valid
      if (notificationService.notificationsCache.data.length > 0 && 
          (now - notificationService.notificationsCache.timestamp) < notificationService.notificationsCache.expiryMs) {
        return notificationService.notificationsCache.data;
      }
      
      // Get notifications from multiple sources
      let apiNotifications = [];
      try {
        apiNotifications = await notificationService.getUnreadNotifications();
      } catch (err) {
        console.warn('Error fetching API notifications, falling back to local sources', err);
      }
      
      // Reduce frequency of sales orders and low stock calls based on configuration
      const shouldFetchAdditionalData = !notificationService.lastAdditionalDataFetch || 
        (now - notificationService.lastAdditionalDataFetch) > NOTIFICATION_CONFIG.additionalDataInterval;
      
      let salesOrderNotifications = [];
      let purchaseOrderNotifications = [];
      let lowStockNotifications = [];
      
      if (shouldFetchAdditionalData) {
        try {
          [salesOrderNotifications, purchaseOrderNotifications, lowStockNotifications] = await Promise.all([
            notificationService.getRecentSalesOrders(),
            notificationService.getRecentPurchaseOrders(),
            notificationService.getLowStockNotifications()
          ]);
          notificationService.lastAdditionalDataFetch = now;
        } catch (err) {
          console.warn('Error fetching additional notification data:', err);
          // Use cached data if available
          salesOrderNotifications = notificationService.cachedSalesOrders || [];
          purchaseOrderNotifications = notificationService.cachedPurchaseOrders || [];
          lowStockNotifications = notificationService.cachedLowStock || [];
        }
      } else {
        // Use cached data
        salesOrderNotifications = notificationService.cachedSalesOrders || [];
        purchaseOrderNotifications = notificationService.cachedPurchaseOrders || [];
        lowStockNotifications = notificationService.cachedLowStock || [];
      }
      
      // Cache the additional data
      notificationService.cachedSalesOrders = salesOrderNotifications;
      notificationService.cachedPurchaseOrders = purchaseOrderNotifications;
      notificationService.cachedLowStock = lowStockNotifications;
      
      // Combine all notifications
      const allNotifications = [
        ...apiNotifications,
        ...salesOrderNotifications,
        ...purchaseOrderNotifications,
        ...lowStockNotifications
      ];
      
      // Filter out duplicates based on ID (for API notifications) and orderId (for sales order notifications)
      const uniqueNotifications = [];
      const seenIds = new Set();
      const seenOrderIds = new Set();
      
      for (const notification of allNotifications) {
        // For regular notifications, check the ID
        if (notification.id && !notification.id.toString().startsWith('low-stock-')) {
          if (!seenIds.has(notification.id)) {
            seenIds.add(notification.id);
            uniqueNotifications.push(notification);
          }
        } 
        // For sales order notifications, check the order ID
        else if (notification.type === 'SALES_ORDER' && notification.data?.orderId) {
          if (!seenOrderIds.has(notification.data.orderId)) {
            seenOrderIds.add(notification.data.orderId);
            uniqueNotifications.push(notification);
          }
        }
        // For low stock notifications, just include the most recent one
        else if (notification.type === 'LOW_STOCK') {
          // Only add if we haven't seen a low stock notification yet
          if (!uniqueNotifications.some(n => n.type === 'LOW_STOCK')) {
            uniqueNotifications.push(notification);
          }
        }
      }
      
      // Format and sort notifications by date (newest first)
      const formattedNotifications = uniqueNotifications
        .map(notificationService.formatNotification)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      // Cache the result
      notificationService.notificationsCache.data = formattedNotifications;
      notificationService.notificationsCache.timestamp = now;
      
      return formattedNotifications;
    } catch (error) {
      console.error('Error getting all notifications:', error);
      return [];
    }
  },

  // Get all notifications with pagination
  getAllNotificationsPaginated: (page = 0, size = 10) => {
    return api.get(`${NOTIFICATION_URL}?page=${page}&size=${size}`);
  },

  // Get only unread notifications
  getUnreadNotifications: () => {
    return api.get(`${NOTIFICATION_URL}/unread`);
  },

  // Mark a notification as read
  markAsRead: (notificationId) => {
    return api.put(`${NOTIFICATION_URL}/${notificationId}/read`);
  },

  // Mark all notifications as read
  markAllAsRead: () => {
    return api.put(`${NOTIFICATION_URL}/read-all`);
  },

  // Delete a notification
  deleteNotification: (notificationId) => {
    return api.delete(`${NOTIFICATION_URL}/${notificationId}`);
  },

  // Create a notification
  createNotification: (notification) => {
    return axios.post(`${API_BASE_URL}/api/notifications`, notification);
  },

  // Add cache properties for additional data
  lastAdditionalDataFetch: 0,
  cachedSalesOrders: [],
  cachedLowStock: [],
  salesOrdersFailureCount: 0,
  lastSalesOrdersFailure: 0,
  lowStockFailureCount: 0,
  lastLowStockFailure: 0
};

export default notificationService; 